import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useSaaSAuth } from '../contexts/SaaSAuthContext';
import { Buildings, AddressBook, CheckCircle, Warning, ArrowUp, Export, Compass, Target, ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

function MetricCard({ icon: Icon, label, value, color = 'gray', sub }) {
  const colorMap = {
    gray: 'bg-gray-50 text-gray-900 border-gray-200',
    green: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    blue: 'bg-blue-50 text-blue-900 border-blue-200',
    red: 'bg-red-50 text-red-900 border-red-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    black: 'bg-gray-900 text-white border-gray-900',
  };
  return (
    <div className={`border rounded-md p-4 ${colorMap[color] || colorMap.gray}`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono uppercase tracking-wider opacity-70">{label}</span>
        <Icon size={18} weight="bold" />
      </div>
      <p className="text-2xl font-mono font-bold tracking-tight">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { logout } = useSaaSAuth();

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/admin/dashboard');
      setMetrics(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Session expired - force re-login
        await logout();
        return;
      }
      setError(err?.response?.data?.detail || err?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">{t('dashboard.loading')}</div>;
  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-4" data-testid="dashboard-error">
      <p className="text-red-600 text-sm">{error}</p>
      <Button onClick={loadDashboard} variant="outline" size="sm"><ArrowsClockwise size={14} className="mr-2" />{t('common.retry')}</Button>
    </div>
  );
  if (!metrics) return (
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <p className="text-gray-500 text-sm">{t('dashboard.noData')}</p>
      <Button onClick={loadDashboard} variant="outline" size="sm"><ArrowsClockwise size={14} className="mr-2" />{t('common.retry')}</Button>
    </div>
  );

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">{t('dashboard.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {/* Onboarding checklist - show when system is early-stage */}
      {(metrics.total_companies < 20 || metrics.pushed_to_campaign === 0) && (
        <div className="border border-gray-200 rounded-md bg-white p-5 mb-8" data-testid="onboarding-checklist">
          <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-4">{t('dashboard.gettingStarted')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { label: t('dashboard.checklist.import'), done: metrics.total_companies > 0, hint: t('dashboard.checklist.importHint'), link: '/app/imports' },
              { label: t('dashboard.checklist.score'), done: metrics.total_companies > 0, hint: t('dashboard.checklist.scoreHint'), link: '/app/scoring' },
              { label: t('dashboard.checklist.review'), done: metrics.approved > 0, hint: t('dashboard.checklist.reviewHint'), link: '/app/review' },
              { label: t('dashboard.checklist.push'), done: metrics.pushed_to_campaign > 0, hint: t('dashboard.checklist.pushHint'), link: '/app/integrations' },
              { label: t('dashboard.checklist.campaign'), done: false, hint: t('dashboard.checklist.campaignHint'), link: null },
            ].map((item, i) => (
              <button
                key={i}
                onClick={() => item.link && (window.location.href = item.link)}
                className={`text-left p-3 rounded-md border transition-colors ${
                  item.done
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center text-[8px] ${item.done ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-300'}`}>
                    {item.done && <CheckCircle size={10} weight="bold" />}
                  </div>
                  <span className="text-xs font-medium">{item.label}</span>
                </div>
                <p className="text-[11px] text-gray-500 ml-6">{item.hint}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Primary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard icon={Buildings} label={t('dashboard.totalCompanies')} value={metrics.total_companies} color="black" />
        <MetricCard icon={AddressBook} label={t('dashboard.totalContacts')} value={metrics.total_contacts} />
        <MetricCard icon={CheckCircle} label={t('dashboard.approved')} value={metrics.approved} color="green" />
        <MetricCard icon={Target} label={t('dashboard.campaignReady')} value={metrics.campaign_ready} color="blue" />
      </div>

      {/* Status breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard icon={ArrowUp} label={t('dashboard.newLeads')} value={metrics.new_leads} color="blue" sub={t('dashboard.today', { count: metrics.new_today })} />
        <MetricCard icon={Warning} label={t('dashboard.needsReview')} value={metrics.needs_review} color="amber" />
        <MetricCard icon={Export} label={t('dashboard.exported')} value={metrics.exported} />
        <MetricCard icon={Compass} label={t('dashboard.pushed')} value={metrics.pushed_to_campaign} color="green" />
      </div>

      {/* Email status + Scoring */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Email Quality */}
        <div className="border border-gray-200 rounded-md bg-white p-5">
          <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-4">{t('dashboard.emailQuality')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">{t('dashboard.validEmails')}</span>
              <span className="font-mono text-sm font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">{metrics.valid_emails}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">{t('dashboard.riskyEmails')}</span>
              <span className="font-mono text-sm font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">{metrics.risky_emails}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">{t('dashboard.unverifiedEmails')}</span>
              <span className="font-mono text-sm font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">{metrics.unverified_emails}</span>
            </div>
          </div>
        </div>

        {/* Industry Breakdown */}
        <div className="border border-gray-200 rounded-md bg-white p-5">
          <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-4">{t('dashboard.industry')}</h3>
          <div className="space-y-2">
            {(metrics.industry_breakdown || []).slice(0, 6).map((ind, i) => (
              <div key={i} className="flex justify-between items-center">
                <span className="text-sm text-gray-700 truncate mr-3">{ind.industry || t('dashboard.uncategorized')}</span>
                <span className="font-mono text-sm font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded-full">{ind.count}</span>
              </div>
            ))}
            {(!metrics.industry_breakdown || metrics.industry_breakdown.length === 0) && (
              <p className="text-sm text-gray-400">{t('dashboard.noIndustry')}</p>
            )}
          </div>
        </div>
      </div>

      {/* System stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-gray-200 rounded-md bg-white p-4 text-center">
          <p className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-1">{t('nav.imports')}</p>
          <p className="text-xl font-mono font-bold text-gray-900">{metrics.total_imports}</p>
        </div>
        <div className="border border-gray-200 rounded-md bg-white p-4 text-center">
          <p className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-1">{t('nav.exports')}</p>
          <p className="text-xl font-mono font-bold text-gray-900">{metrics.total_exports}</p>
        </div>
        <div className="border border-gray-200 rounded-md bg-white p-4 text-center">
          <p className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-1">{t('dashboard.discoveryJobs')}</p>
          <p className="text-xl font-mono font-bold text-gray-900">{metrics.total_discovery_jobs}</p>
        </div>
        <div className="border border-gray-200 rounded-md bg-white p-4 text-center">
          <p className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-1">{t('dashboard.rejected')}</p>
          <p className="text-xl font-mono font-bold text-gray-900">{metrics.rejected}</p>
        </div>
      </div>
    </div>
  );
}
